import { keepPreviousData } from '@tanstack/query-core'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import queryString from 'query-string'
import { useDispatch } from 'react-redux'
import { UserStatus } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { PaginationResponse } from 'src/models/pagination.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { SimpleResponse, userQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  RequestConfig,
  useExtendInfiniteQuery,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  GET_ME_FAILURE,
  GET_ME_INIT,
  GET_ME_SUCCESS,
  postUserWithoutPhoneVerificationAndSignIn,
} from 'src/redux/actions/auth.actions'
import {
  GET_USER_FAILURE,
  GET_USER_INIT,
  GET_USER_SUCCESS,
  GET_USERS_FAILURE,
  GET_USERS_INIT,
  GET_USERS_SUCCESS,
} from 'src/redux/actions/data/users.actions'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'
import { PatchUserRequest } from 'src/requests/patchUser.request'

type QueryOwnUserOptions = ExtendBaseQueryOptions<
  UserListResponse,
  UserListModel
>

const selectQueryUserModel = (data: UserListResponse): UserListModel =>
  new UserListModel(data)
export const useQueryOwnUser = ({
  enabled = true,
  ...rest
}: QueryOwnUserOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery({
    queryKey: userQueryKeys.me(),
    queryUrl: () => 'users/me',
    select: selectQueryUserModel,
    enabled,
    // TODO: what is an appropriate stale time for the authed user?
    staleTime: 1000 * 60 * 5,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_ME_INIT },
    onSuccess: (data) => {
      dispatch({
        type: GET_ME_SUCCESS,
        payload: data,
        meta: { source: 'react-query' },
      })
    },
    onError: (error) => {
      // Skip clearing auth state when the 401 is PHONE_NOT_VERIFIED —
      // the user just needs to verify their new phone number, not re-login.
      // Clearing the JWT here would trigger cognitoSessionValid → signout.
      const errorData = (error as { response?: { data?: { error?: string } } })
        ?.response?.data
      if (errorData?.error === UserStatus.PhoneNotVerified) {
        return
      }
      dispatch({ type: GET_ME_FAILURE, payload: error })
    },
  })
}

export const usePatchUser = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    UserListResponse,
    { body: Partial<PatchUserRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/users`,
    onSuccess: () => {
      // invalidate user
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
    },
  })
}

interface QueryUserOptions
  extends ExtendBaseQueryOptions<UserListResponse, UserListModel> {
  userId: string | undefined
}
export const useQueryUser = ({
  userId,
  enabled = true,
  ...rest
}: QueryUserOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery<UserListResponse, UserListModel>({
    queryKey: userQueryKeys.detail(userId),
    queryUrl: () => `users/${userId}`,
    enabled: enabled && userId !== undefined,
    select: selectQueryUserModel,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_USER_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_USER_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_USER_FAILURE, payload: error })
    },
  })
}

interface GetUsersResponse {
  users: UserListResponse[]
  pagination: PaginationResponse
}

interface GetUsersDataModel {
  users: UserListModel[] | undefined
  page: number | undefined
  totalItems: number | undefined
}

const selectUsersSearchListModel = (
  response: InfiniteData<GetUsersResponse, number>,
): GetUsersDataModel => ({
  users: response?.pages.flatMap((page) =>
    page.users.map((user) => new UserListModel(user)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

interface QueryUsersSearchOptions
  extends ExtendBaseInfiniteQueryOptions<
    GetUsersResponse,
    number,
    GetUsersDataModel
  > {
  filters: GetUsersSearchRequest
  additionalKey?: string
}

export const useQueryUsersSearch = ({
  filters,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryUsersSearchOptions) => {
  const dispatch = useDispatch()
  return useExtendInfiniteQuery({
    queryKey: userQueryKeys.search(filters, rest.additionalKey),
    queryUrl: ({ pageParam }) => {
      return `users/search?${queryString.stringify({ ...filters, page: pageParam })}`
    },
    enabled: enabled && filters !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    select: selectUsersSearchListModel,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...queryCacheOptions,
    ...rest,
    // react query <-> redux interop
    reduxInitAction: { type: GET_USERS_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_USERS_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_USERS_FAILURE, payload: error })
    },
  })
}

export const useUserSkipPhoneVerification = (
  email: string,
  password: string,
  acceptOrgInvite?: boolean,
) => {
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<
    { response: SimpleResponse },
    { body: { email: string } }
  >(MutationMethod.POST, {
    mutationUrl: () => 'users/phone/skipverification',
    onSuccess: () => {
      dispatch(
        postUserWithoutPhoneVerificationAndSignIn(
          email,
          password,
          acceptOrgInvite,
        ),
      )
      dispatch(createSetIsExtraVerificationPopupOpen(false))
    },
    requestConfig: RequestConfig.Unauthed,
  })
}

export const useUserDeletePhoneNumber = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<{ response: SimpleResponse }, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => 'users/phone/delete',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
      },
    },
  )
}
